import React, { useEffect, useMemo, useState } from 'react';
import { Button, Col, Input, Modal, Row, Table, Typography, message } from 'antd';
import { EditOutlined, ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import { postCall } from '../../utils/network.util';
import { GET_REDBUS_CITY_LIST_URL, UPDATE_REDBUS_CITY_ALIAS_URL } from '../../utils/api-url';
import { dafidyUserCheck } from '../../utils/constant';
import { isValid, isValidArray } from '../../utils/utilities';
import './redbus-cities.page.style.scss';

const { Text } = Typography;

export const RedbusCitiesPage = () => {
  const isDafidyUser = dafidyUserCheck();
  const [loading, setLoading] = useState(false);
  const [cities, setCities] = useState([]);
  const [searchText, setSearchText] = useState('');

  const [selectedCity, setSelectedCity] = useState(null);
  const [aliasName, setAliasName] = useState('');

  const fetchCities = async () => {
    setLoading(true);
    try {
      const res = await postCall(GET_REDBUS_CITY_LIST_URL, {});
      if (res?.status === 'SUCCESS' && res?.payload?.Status === 'SUCCESS') {
        const list = res.payload.Payload;
        setCities(isValidArray(list) ? list : []);
      } else {
        setCities([]);
        message.error(res?.payload?.Message || 'Failed to fetch redbus cities');
      }
    } catch (e) {
      console.error(e);
      setCities([]);
      message.error('Failed to fetch redbus cities');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isDafidyUser) {
      fetchCities();
    }
  }, []);

  const filteredCities = useMemo(() => {
    const searchKey = String(searchText || '').trim().toLowerCase();
    if (searchKey === '') {
      return cities;
    }
    return cities.filter((city) => {
      const cityName = String(city?.city_name || '').toLowerCase();
      const alias = String(city?.alias_city_name || '').toLowerCase();
      return cityName.includes(searchKey) || alias.includes(searchKey);
    });
  }, [cities, searchText]);

  const onEditClick = (city) => {
    setSelectedCity(city);
    setAliasName(isValid(city?.alias_city_name) ? city.alias_city_name : '');
  };

  const onCancelEdit = () => {
    setSelectedCity(null);
    setAliasName('');
  };

  const onUpdateAlias = async () => {
    const alias = String(aliasName || '').trim();
    setLoading(true);
    try {
      const res = await postCall(UPDATE_REDBUS_CITY_ALIAS_URL, {
        id: selectedCity?.id,
        aliasCityName: alias
      });
      if (res?.status === 'SUCCESS' && res?.payload?.Status === 'SUCCESS') {
        const list = res.payload.Payload;
        setCities(isValidArray(list) ? list : []);
        message.success('Alias name updated');
        onCancelEdit();
      } else {
        message.error(res?.payload?.Message || 'Failed to update alias name');
      }
    } catch (e) {
      console.error(e);
      message.error('Failed to update alias name');
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: 'City Name',
      dataIndex: 'city_name',
      key: 'city_name',
      render: (text) => <span className="redbus-city-name">{text}</span>
    },
    {
      title: 'Redbus City Id',
      dataIndex: 'rb_city_id',
      key: 'rb_city_id',
      width: 160,
      render: (text) => <span className="redbus-city-id">{text}</span>
    },
    {
      title: 'Alias Name',
      dataIndex: 'alias_city_name',
      key: 'alias_city_name',
      render: (text) => (
        isValid(text)
          ? <span className="redbus-city-alias">{text}</span>
          : <span className="redbus-city-alias-empty">Not set</span>
      )
    },
    {
      title: 'Action',
      key: 'action',
      width: 150,
      align: 'right',
      render: (text, record) => (
        <Button
          className="redbus-city-edit-btn"
          icon={<EditOutlined />}
          onClick={() => onEditClick(record)}
        >
          Edit Alias
        </Button>
      )
    }
  ];

  if (!isDafidyUser) {
    return null;
  }

  return (
    <div className="redbus-cities-page-root">
      <Row className="redbus-cities-header" justify="space-between" align="middle">
        <Col>
          <div className="redbus-cities-title">Redbus Cities</div>
          <div className="redbus-cities-sub-title">Update alias names of redbus cities</div>
        </Col>
        <Col>
          <Button icon={<ReloadOutlined />} onClick={fetchCities} loading={loading}>
            Refresh
          </Button>
        </Col>
      </Row>

      <Row className="redbus-cities-toolbar" justify="space-between" align="middle" gutter={[12, 12]}>
        <Col flex="auto">
          <Input
            className="redbus-cities-search"
            allowClear
            prefix={<SearchOutlined className="redbus-cities-search-icon" />}
            placeholder="Search by city name or alias name"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
        </Col>
        <Col>
          <span className="redbus-cities-count">
            {`${filteredCities.length} of ${cities.length} cities`}
          </span>
        </Col>
      </Row>

      <Table
        className="redbus-cities-table"
        rowKey="id"
        size="middle"
        loading={loading}
        columns={columns}
        dataSource={filteredCities}
        locale={{ emptyText: 'No redbus cities found' }}
        pagination={{ defaultPageSize: 20, showSizeChanger: true, size: 'small' }}
        scroll={{ x: 'max-content' }}
      />

      <Modal
        className="redbus-cities-modal"
        title="Update alias name"
        visible={selectedCity !== null}
        okText="Update"
        onOk={onUpdateAlias}
        onCancel={onCancelEdit}
        confirmLoading={loading}
        destroyOnClose
      >
        <div className="redbus-cities-modal-city">
          <span className="redbus-cities-modal-label">City name</span>
          <span className="redbus-cities-modal-value">{selectedCity?.city_name || ''}</span>
        </div>
        <label className="redbus-cities-modal-field-label">Alias name</label>
        <Input
          autoFocus
          placeholder="Alias name"
          value={aliasName}
          onChange={(e) => setAliasName(e.target.value)}
          onPressEnter={onUpdateAlias}
          maxLength={100}
        />
        <Text className="redbus-cities-modal-hint">
          Leave it empty to remove the existing alias name.
        </Text>
      </Modal>
    </div>
  );
};
